import React, { useState, useEffect } from "react";
import { permissionService } from "../services/permissionService";
import { userService } from "../services/userService";

const PermissionMatrix = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [permissionSequence, setPermissionSequence] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load roles and permissions from database
      const [rolesRes, permissionsRes] = await Promise.all([
        userService.getRoles(), // Loads roles from database
        permissionService.getPermissions(), // Loads permissions from database
      ]);

      setRoles(rolesRes.data.data);
      setPermissions(permissionsRes.data.data);
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setLoading(false);
  };

  const loadRolePermissions = async (roleId) => {
    try {
      const response = await permissionService.getRolePermissions(roleId);
      const permissionsMap = {};
      const sequenceMap = {};
      let sequenceNumber = 1;

      // Filter and sort assigned permissions by creation order
      const assignedPermissions = response.data.data
        .filter((perm) => perm.has_permission === 1 || perm.allowed === 1)
        .sort((a, b) => {
          // Sort by assigned_at if available, otherwise by permission id
          if (a.assigned_at && b.assigned_at) {
            return new Date(a.assigned_at) - new Date(b.assigned_at);
          }
          return a.id - b.id;
        });

      // Assign sequence numbers to assigned permissions
      assignedPermissions.forEach((perm) => {
        sequenceMap[perm.id] = sequenceNumber++;
      });

      // Build permissions map
      response.data.data.forEach((perm) => {
        permissionsMap[perm.id] =
          perm.has_permission === 1 || perm.allowed === 1;
      });

      setRolePermissions(permissionsMap);
      setPermissionSequence(sequenceMap);
      setSelectedRole(roleId);
    } catch (error) {
      console.error("Error loading role permissions:", error);
    }
  };

  const togglePermission = (permissionId) => {
    const wasAssigned = rolePermissions[permissionId];

    setRolePermissions((prev) => ({
      ...prev,
      [permissionId]: !prev[permissionId],
    }));

    // Handle sequence updates
    if (wasAssigned) {
      // Remove from sequence if unchecking
      setPermissionSequence((prev) => {
        const newSeq = { ...prev };
        delete newSeq[permissionId];
        // Reorder remaining sequences
        const sorted = Object.entries(newSeq)
          .sort(([, a], [, b]) => a - b)
          .map(([id], index) => [id, index + 1]);
        return Object.fromEntries(sorted);
      });
    } else {
      // Add to end of sequence if checking
      setPermissionSequence((prev) => {
        const maxSeq =
          Object.values(prev).length > 0 ? Math.max(...Object.values(prev)) : 0;
        return {
          ...prev,
          [permissionId]: maxSeq + 1,
        };
      });
    }
  };

  const movePermissionUp = (permissionId) => {
    const currentSeq = permissionSequence[permissionId];
    if (!currentSeq || currentSeq === 1) return; // Already first or not assigned

    setPermissionSequence((prev) => {
      const newSeq = { ...prev };
      // Find permission with sequence - 1
      const prevPermissionId = Object.keys(newSeq).find(
        (id) => newSeq[id] === currentSeq - 1
      );

      if (prevPermissionId) {
        // Swap sequences
        newSeq[permissionId] = currentSeq - 1;
        newSeq[prevPermissionId] = currentSeq;
      }
      return newSeq;
    });
  };

  const movePermissionDown = (permissionId) => {
    const currentSeq = permissionSequence[permissionId];
    if (!currentSeq) return; // Not assigned

    const seqValues = Object.values(permissionSequence);
    if (seqValues.length === 0) return;
    const maxSeq = Math.max(...seqValues);
    if (currentSeq === maxSeq) return; // Already last

    setPermissionSequence((prev) => {
      const newSeq = { ...prev };
      // Find permission with sequence + 1
      const nextPermissionId = Object.keys(newSeq).find(
        (id) => newSeq[id] === currentSeq + 1
      );

      if (nextPermissionId) {
        // Swap sequences
        newSeq[permissionId] = currentSeq + 1;
        newSeq[nextPermissionId] = currentSeq;
      }
      return newSeq;
    });
  };

  const handleSave = async () => {
    if (!selectedRole) return;

    setSaving(true);
    try {
      // Get assigned permissions sorted by sequence
      const assignedPermissions = Object.entries(rolePermissions)
        .filter(([_, allowed]) => allowed)
        .map(([permissionId, _]) => ({
          id: parseInt(permissionId),
          sequence: permissionSequence[parseInt(permissionId)] || 999,
        }))
        .sort((a, b) => a.sequence - b.sequence)
        .map((p) => p.id);

      await permissionService.updateRolePermissions(
        selectedRole,
        assignedPermissions
      );

      alert("Permissions saved successfully!");
      // Reload to get updated sequence
      await loadRolePermissions(selectedRole);
    } catch (error) {
      console.error("Error saving permissions:", error);
      alert("Error saving permissions");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="p-8 ">
      <div className="max-w-6xl mx-auto p-6">
        <h2 className="text-2xl font-bold mb-6">Permission Matrix</h2>
        <p className="text-gray-600 mb-6">
          Select a role and assign permissions to it. To manage roles and
          permissions, use the Roles Management and Permissions Management pages
          from the sidebar.
        </p>

        {/* Permission Assignment Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Role
            </label>
            <select
              value={selectedRole || ""}
              onChange={(e) => loadRolePermissions(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 min-w-[200px]"
            >
              <option value="">-- Select Role --</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.role_name}
                </option>
              ))}
            </select>
          </div>

          {selectedRole && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-teal-600 to-emerald-600">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Sequence
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Permission
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                        Allowed
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {permissions.map((permission) => {
                      const isAssigned =
                        rolePermissions[permission.id] || false;
                      const sequence = permissionSequence[permission.id];
                      return (
                        <tr
                          key={permission.id}
                          className={isAssigned ? "bg-blue-50" : ""}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center gap-1">
                              {isAssigned && sequence ? (
                                <>
                                  <button
                                    onClick={() =>
                                      movePermissionUp(permission.id)
                                    }
                                    disabled={sequence === 1}
                                    className="p-1 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                                    title="Move Up"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-4 w-4"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 15l7-7 7 7"
                                      />
                                    </svg>
                                  </button>
                                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold">
                                    {sequence}
                                  </span>
                                  <button
                                    onClick={() =>
                                      movePermissionDown(permission.id)
                                    }
                                    disabled={
                                      sequence ===
                                      (Object.values(permissionSequence)
                                        .length > 0
                                        ? Math.max(
                                            ...Object.values(permissionSequence)
                                          )
                                        : 0)
                                    }
                                    className="p-1 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                                    title="Move Down"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-4 w-4"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                      />
                                    </svg>
                                  </button>
                                </>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {isAssigned && sequence === 1 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  First
                                </span>
                              )}
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {permission.permission_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {permission.permission_key}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <input
                              type="checkbox"
                              checked={isAssigned}
                              onChange={() => togglePermission(permission.id)}
                              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-6">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Permissions"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionMatrix;
